import { DomainHit, FilterState } from './types';

/**
 * Calculate weighted score for a domain
 * Used citations + (weight * considered citations)
 */
export const calculateWeightedScore = (
  hit: DomainHit,
  consideredWeight: number = 0.25
): number => {
  return hit.usedBy.size + (consideredWeight * hit.consideredBy.size);
};

/**
 * Calculate coverage percentage
 * (domains used in queries / total queries) * 100
 */
export const calculateCoveragePercent = (
  hit: DomainHit,
  totalQueries: number
): number => {
  if (totalQueries === 0) return 0;
  return (hit.usedBy.size / totalQueries) * 100;
};

/**
 * Apply all filters and sorting to domain data
 */
export const applyFiltersAndSort = (
  domainMap: Map<string, DomainHit>,
  filters: FilterState,
  totalQueries: number
): DomainHit[] => {
  let domains = Array.from(domainMap.values());

  // Filter by focused query
  if (filters.focusedQuery) {
    domains = domains.filter(
      hit => hit.usedBy.has(filters.focusedQuery!) || hit.consideredBy.has(filters.focusedQuery!)
    );
  }

  // Filter by providers (show domains cited by selected providers)
  if (filters.providers.size > 0 && filters.providers.size < 2) {
    domains = domains.filter(hit => {
      return Array.from(filters.providers).some(provider => hit.providers.has(provider));
    });
  }

  // Exclusive mode: show only domains cited by ALL selected providers
  if (filters.exclusiveMode && filters.providers.size > 1) {
    domains = domains.filter(hit => {
      return Array.from(filters.providers).every(provider => hit.providers.has(provider));
    });
  }

  // Filter by TLD
  if (filters.tldFilters.gov) {
    domains = domains.filter(hit => hit.domain.endsWith('.gov'));
  }
  if (filters.tldFilters.edu) {
    domains = domains.filter(hit => hit.domain.endsWith('.edu'));
  }
  if (filters.tldFilters.org) {
    domains = domains.filter(hit => hit.domain.endsWith('.org'));
  }

  // Filter by access
  if (filters.accessFilter === 'accessible') {
    domains = domains.filter(hit => hit.tag === 'accessible');
  } else if (filters.accessFilter === 'paywalled') {
    domains = domains.filter(hit => hit.tag === 'paywalled');
  }

  // Filter by minimum coverage
  if (filters.minCoverage > 0) {
    domains = domains.filter(hit => {
      const coverage = calculateCoveragePercent(hit, totalQueries);
      return coverage >= filters.minCoverage;
    });
  }

  // Sort
  domains.sort((a, b) => {
    let aValue = 0;
    let bValue = 0;

    if (filters.sortBy === 'used') {
      aValue = a.usedBy.size;
      bValue = b.usedBy.size;
    } else if (filters.sortBy === 'coverage') {
      aValue = calculateCoveragePercent(a, totalQueries);
      bValue = calculateCoveragePercent(b, totalQueries);
    }

    // Apply weighted score for sorting
    const aWeighted = calculateWeightedScore(a, filters.consideredWeight);
    const bWeighted = calculateWeightedScore(b, filters.consideredWeight);

    // Primary sort by selected metric
    const primaryDiff = filters.sortDir === 'desc' ? bValue - aValue : aValue - bValue;
    
    // Secondary sort by weighted score
    if (primaryDiff === 0) {
      return filters.sortDir === 'desc' ? bWeighted - aWeighted : aWeighted - bWeighted;
    }

    return primaryDiff;
  });

  return domains;
};
